import { ReactNode } from "react";
import classNames from "classnames";
import { noop } from "@common/helpers/function";
import { AccountStatusIcon, LinkIcon, SourceIcon, TriangleIcon } from "@icons";
import { AggregatedFilters } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters";
import { AggregatedFiltersOverlay } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersOverlay";
import { AggregatedFiltersProvider } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersProvider";
import { FilterNavTab } from "@components/pageHeader/filters/FilterNavTab";
import { FilterTabPanel } from "@components/pageHeader/filters/FilterTabPanel";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import overlayStyles from "@components/pageHeader/filters/FiltersOverlay.module.scss";
import statusStyles from "@components/pageHeader/filters/Tabs/TransactionStatusFilter/TransactionStatusFilter.module.scss";
import type { PreviewOriginEntity } from "./cells/ApplyCoaPreviewNameCell";
import {
    ApplyCoaPreviewOriginSource,
    ApplyCoaPreviewSource,
    ApplyCoaPreviewUpdate,
} from "./applyCoaTemplatePreviewStatus";
import {
    ORIGIN_TAB,
    SOURCE_OPTIONS,
    SOURCE_TAB,
    UPDATE_OPTIONS,
    UPDATES_TAB,
} from "./applyCoaPreviewFilterOptions";
import { ApplyCoaPreviewActiveFilters } from "./ApplyCoaPreviewActiveFilters";
import { ApplyCoaPreviewOriginFilterPanel } from "./ApplyCoaPreviewOriginFilter";

// Preview filter built from the app's standard "Add filter" primitives
// (AggregatedFilters popover + FilterNavTab/FilterTabPanel) so it looks and
// behaves like every other Add-filter in the app, instead of a bespoke dropdown.
// Origin / Updates / Source are preview-local, so they live here rather than
// touching the shared Filters/FiltersOverlay/FiltersTypes infrastructure.

const PREVIEW_UPDATES: string[] = Object.values(ApplyCoaPreviewUpdate);
const PREVIEW_SOURCES: string[] = Object.values(ApplyCoaPreviewSource);

function isPreviewUpdate(
    value: FilterMultiselectValue,
): value is ApplyCoaPreviewUpdate {
    return typeof value === "string" && PREVIEW_UPDATES.includes(value);
}

function isPreviewSource(
    value: FilterMultiselectValue,
): value is ApplyCoaPreviewSource {
    return typeof value === "string" && PREVIEW_SOURCES.includes(value);
}

// Resolves the multiselect change into a single selected value (the newly added
// option replaces the current one); empty selection resets to the "all" value.
function resolveSingleSelect<T extends string>({
    values,
    current,
    allValue,
    isValid,
}: {
    values: FilterMultiselectValue[];
    current: T;
    allValue: T;
    isValid: (value: FilterMultiselectValue) => value is T;
}): T {
    if (values.length === 0) {
        return allValue;
    }
    const added = values.find(
        (entry): entry is T => entry !== current && isValid(entry),
    );
    const first = values[0];
    const fallback = isValid(first) ? first : allValue;
    return added ?? fallback;
}

const PreviewFilterNavTab = ({
    tabKey,
    label,
    icon,
}: Readonly<{
    tabKey: string;
    label: string;
    icon: ReactNode;
}>) => (
    <FilterNavTab eventKey={tabKey} className={overlayStyles.navigationButton}>
        <span className={overlayStyles.navigationItem}>
            <span className={overlayStyles.navigationIcon}>{icon}</span>
            {label}
            <TriangleIcon
                className={classNames(
                    overlayStyles.navigationActiveIndicator,
                    "icon-color-gray-500",
                )}
            />
        </span>
    </FilterNavTab>
);

const PreviewFilterPanel = <T extends string>({
    tabKey,
    options,
    value,
    allValue,
    isValid,
    name,
    onChange,
}: Readonly<{
    tabKey: string;
    options: FilterMultiselectOption[];
    value: T;
    allValue: T;
    isValid: (entry: FilterMultiselectValue) => entry is T;
    name: string;
    onChange: (value: T) => void;
}>) => {
    // Empty selection means "all" (mirrors the shared AccountStatusFilter).
    const selected = value === allValue ? [] : [value];

    return (
        <FilterTabPanel eventKey={tabKey}>
            <div className={statusStyles.filter}>
                <FilterMultiselect
                    inDropdown={false}
                    options={options}
                    value={selected}
                    onChange={(values) =>
                        onChange(
                            resolveSingleSelect({
                                values,
                                current: value,
                                allValue,
                                isValid,
                            }),
                        )
                    }
                    name={name}
                    onBlur={noop}
                />
            </div>
        </FilterTabPanel>
    );
};

interface Props {
    originSources: ApplyCoaPreviewOriginSource[];
    onOriginSourcesChange: (sources: ApplyCoaPreviewOriginSource[]) => void;
    // The previewed company, used to label the "entity" origin option with its
    // name + avatar. Absent in the AddClient setup preview (no diff).
    originEntity?: PreviewOriginEntity;
    updates: ApplyCoaPreviewUpdate[];
    onUpdatesChange: (updates: ApplyCoaPreviewUpdate[]) => void;
    source: ApplyCoaPreviewSource;
    onSourceChange: (source: ApplyCoaPreviewSource) => void;
    // The historical Activity drawer has no template<>entity diff, so Origin is
    // not a meaningful (or truthful) filter there; it is hidden.
    showOrigin?: boolean;
}

export const ApplyCoaPreviewFilters = ({
    originSources,
    onOriginSourcesChange,
    originEntity,
    updates,
    onUpdatesChange,
    source,
    onSourceChange,
    showOrigin = true,
}: Props) => (
    <AggregatedFiltersProvider
        defaultTab={showOrigin ? ORIGIN_TAB : UPDATES_TAB}
    >
        <AggregatedFilters>
            <AggregatedFiltersOverlay
                filterClassname={overlayStyles.filter}
                navigation={
                    <>
                        {showOrigin && (
                            <PreviewFilterNavTab
                                tabKey={ORIGIN_TAB}
                                label="Origin"
                                icon={<LinkIcon />}
                            />
                        )}
                        <PreviewFilterNavTab
                            tabKey={UPDATES_TAB}
                            label="Updates"
                            icon={<AccountStatusIcon />}
                        />
                        <PreviewFilterNavTab
                            tabKey={SOURCE_TAB}
                            label="Source"
                            icon={<SourceIcon />}
                        />
                    </>
                }
                content={
                    <>
                        {showOrigin && (
                            <ApplyCoaPreviewOriginFilterPanel
                                tabKey={ORIGIN_TAB}
                                originSources={originSources}
                                originEntity={originEntity}
                                onOriginSourcesChange={onOriginSourcesChange}
                            />
                        )}
                        <FilterTabPanel eventKey={UPDATES_TAB}>
                            <div className={statusStyles.filter}>
                                <FilterMultiselect
                                    inDropdown={false}
                                    options={UPDATE_OPTIONS}
                                    value={updates}
                                    onChange={(values) =>
                                        onUpdatesChange(
                                            values.filter(
                                                (
                                                    v,
                                                ): v is ApplyCoaPreviewUpdate =>
                                                    isPreviewUpdate(v) &&
                                                    v !==
                                                        ApplyCoaPreviewUpdate.all,
                                            ),
                                        )
                                    }
                                    name="Updates"
                                    onBlur={noop}
                                />
                            </div>
                        </FilterTabPanel>
                        <PreviewFilterPanel
                            tabKey={SOURCE_TAB}
                            options={SOURCE_OPTIONS}
                            value={source}
                            allValue={ApplyCoaPreviewSource.all}
                            isValid={isPreviewSource}
                            name="Source"
                            onChange={onSourceChange}
                        />
                    </>
                }
            />
        </AggregatedFilters>
        <ApplyCoaPreviewActiveFilters
            originSources={originSources}
            onOriginSourcesChange={onOriginSourcesChange}
            originEntity={originEntity}
            updates={updates}
            onUpdatesChange={onUpdatesChange}
            source={source}
            onSourceChange={onSourceChange}
        />
    </AggregatedFiltersProvider>
);
